import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaWallet, FaPlus, FaFilePdf } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Wallet.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const formatDateDisplay = (input) => {
  try {
    if (!input) return '--';
    return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(input));
  } catch { return '--'; }
};

const toMs = (input) => {
  if (!input) return 0;
  if (typeof input === 'string') return new Date(input).getTime();
  if (input instanceof Date) return input.getTime();
  return 0;
};

const formatKES = (amount) => {
  const n = parseFloat(amount);
  if (isNaN(n)) return '0.00';
  return n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const monthStartMs = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
};

const txLabel = (type) => {
  if (type === 1 || type === '1') return 'DEPOSIT';
  if (type === 0 || type === '0') return 'PREMIUM PAYMENT';
  return 'TRANSACTION';
};

const txClass = (type) => {
  if (type === 1 || type === '1') return 'deposit';
  if (type === 0 || type === '0') return 'payment';
  return 'other';
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Wallet() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');

  // ── Wallet ──
  const [walletNo, setWalletno] = useState('');
  const [balance, setBalance] = useState(null);   // null = not yet loaded
  const [balanceLoading, setBalanceLoading] = useState(true);

  // ── Statements ──
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // ── Date filter ──
  const [startMs, setStartMs] = useState(null);
  const [endMs, setEndMs] = useState(null);
  const [monthName, setMonthname] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Top-up modal ──
  const [modal, setModal] = useState(false);
  const [mobile, setMobile] = useState('');
  const [amount, setAmount] = useState('');
  const [toppingUp, setToppingUp] = useState(false);

  // ── PDF export ──
  const [exporting, setExporting] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(credentials ? { Authorization: `Basic ${credentials}` } : {}),
  }), [credentials]);

  // ══════════════════════════════════════
  //  CREDENTIALS
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, []);

  // ══════════════════════════════════════
  //  AGENT FROM LOCAL STORAGE
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) {
      setAgent(user);
    } else {
      navigate('/login');
    }
    setMonthname(new Date().toLocaleString('default', { month: 'long' }));
  }, [navigate]);

  // ══════════════════════════════════════
  //  AGENT CODE
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers: authHeaders() });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
      } catch {
        toast.error('Failed to load agent details.');
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  WALLET NUMBER
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agentCode || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getWallet?agent=${agentCode}`, { headers: authHeaders() });
        const data = await res.json();
        setWalletno(data.WalletNo);
      } catch {
        toast.error('Failed to load wallet details.');
      }
    })();
  }, [agentCode, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  STATEMENTS + BALANCE (once walletNo known)
  // ══════════════════════════════════════
  const fetchWalletData = useCallback(async () => {
    if (!walletNo || !isAuth) return;
    setLoading(true);
    setBalanceLoading(true);
    setFetchError('');
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/getWalletstatement?agent=${agentCode}`, { headers: authHeaders() }),
        fetch(`${API}/getWalletbalance?agent=${agentCode}`, { headers: authHeaders() }),
      ]);
      const stmts = await r1.json();
      const bal = await r2.json();
      setStatements(Array.isArray(stmts) ? stmts : []);
      setBalance(bal.Balance ?? 0);
    } catch {
      setFetchError('Failed to load wallet data. Please try again.');
    } finally {
      setLoading(false);
      setBalanceLoading(false);
    }
  }, [walletNo, isAuth, authHeaders]);

  useEffect(() => { fetchWalletData(); }, [fetchWalletData]);

  // ══════════════════════════════════════
  //  DATE FILTER HANDLERS
  // ══════════════════════════════════════
  const handleStartDate = (e) => {
    const ms = new Date(e.target.value).getTime();
    setStartMs(ms);
    setMonthname(new Date(ms).toLocaleString('default', { month: 'long' }));
    setCurrentPage(1);
  };

  const handleEndDate = (e) => {
    setEndMs(new Date(e.target.value).getTime());
    setCurrentPage(1);
  };

  // ══════════════════════════════════════
  //  FILTERED DATA (client-side)
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    const defaultStart = monthStartMs();
    return statements.filter(s => {
      const ms = toMs(s.DateDone);
      if (startMs !== null && endMs !== null) return ms >= startMs && ms <= endMs;
      if (startMs !== null) return ms >= startMs;
      return ms >= defaultStart;
    });
  }, [statements, startMs, endMs]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const currentRows = useMemo(
    () => filtered.slice(indexOfFirst, indexOfFirst + ROWS_PER_PAGE),
    [filtered, indexOfFirst]
  );
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ── Summary totals for filtered period ──
  const totals = useMemo(() => ({
    deposits: filtered.filter(s => s.TransactionType === 1 || s.TransactionType === '1').reduce((sum, s) => sum + (parseFloat(s.Amount) || 0), 0),
    payments: filtered.filter(s => s.TransactionType === 0 || s.TransactionType === '0').reduce((sum, s) => sum + (parseFloat(s.Amount) || 0), 0),
    count: filtered.length,
  }), [filtered]);

  useEffect(() => { setCurrentPage(1); }, [startMs, endMs]);

  // ══════════════════════════════════════
  //  PDF EXPORT
  // ══════════════════════════════════════
  const exportPDF = useCallback(() => {
    setExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text(`${monthName.toUpperCase()} WALLET STATEMENT`, doc.internal.pageSize.getWidth() / 2, 14, { align: 'center' });
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Wallet: ${walletNo}`, doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' });

      doc.autoTable({
        head: [['#', 'Date', 'Transaction Type', 'Amount (KES)', 'Balance (KES)', 'Purpose']],
        body: filtered.map((s, i) => [
          i + 1,
          formatDateDisplay(s.DateDone),
          txLabel(s.TransactionType),
          formatKES(s.Amount),
          formatKES(s.Balance),
          s.Purpose ?? '--',
        ]),
        startY: 26,
        styles: { fontSize: 8.5, cellPadding: 2.5 },
        headStyles: { fillColor: [26, 92, 90], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 250, 249] },
        foot: [['', '', 'TOTALS', formatKES(totals.deposits - totals.payments), '', '']],
        footStyles: { fillColor: [240, 247, 246], textColor: [26, 92, 90], fontStyle: 'bold' },
      });

      doc.save(`wallet_statement_${monthName.toLowerCase()}.pdf`);
    } catch {
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [filtered, monthName, walletNo, totals]);

  // ══════════════════════════════════════
  //  TOP-UP (currently disabled)
  // ══════════════════════════════════════
  const handleTopup = () => {
    toast.error('Top-up is currently disabled. Please contact support.');
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="wallet-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="wallet-main">
        {/* Breadcrumb */}
        <div className="wallet-top-row">
          <span className="wallet-company">Definite Assurance</span>
          <span className="wallet-breadcrumb">Dashboard &rsaquo; My Wallet</span>
        </div>

        {/* ── Balance Hero ── */}
        <div className="wallet-hero">
          <div className="wallet-hero-left">
            <span className="wallet-hero-label">Wallet Balance</span>
            <span className="wallet-hero-balance">
              {balanceLoading ? '———' : `KES ${formatKES(balance)}`}
            </span>
            {walletNo && <span className="wallet-hero-no">Wallet No: {walletNo}</span>}
          </div>
          <button className="wallet-topup-btn" onClick={() => setModal(true)}>
            <FaPlus /> Top Up Wallet
          </button>
        </div>

        {/* ── Statement Card ── */}
        <div className="wallet-card">
          <div className="wallet-card-header">
            <h3><FaWallet /> Wallet Statement — {monthName}</h3>
          </div>

          {/* Toolbar */}
          <div className="wallet-toolbar">
            <div className="wallet-date-group">
              <label>From Date</label>
              <input type="date" onChange={handleStartDate} />
            </div>
            <div className="wallet-date-group">
              <label>To Date</label>
              <input type="date" onChange={handleEndDate} />
            </div>
            <button
              className="wallet-export-btn"
              onClick={exportPDF}
              disabled={exporting || filtered.length === 0}
            >
              <FaFilePdf /> {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
          </div>

          {/* Summary strip */}
          {!loading && filtered.length > 0 && (
            <div className="wallet-summary">
              <div className="wallet-summary-item">
                <span>Transactions</span>
                <span>{totals.count}</span>
              </div>
            
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="wallet-loading">
              <div className="wallet-spinner" />
              <p>Loading wallet statement…</p>
            </div>
          ) : fetchError ? (
            <div className="wallet-error-state">
              <p>{fetchError}</p>
              <button onClick={fetchWalletData}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="wallet-empty">
              No transactions found for the selected period.
            </div>
          ) : (
            <>
              <div className="wallet-table-wrap">
                <table className="wallet-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Policy Number</th>
                       <th>Registration</th>
                      <th>Client</th>
                      <th>Commencement Date</th>
                      <th>Expiry Date</th>
                      <th>Amount (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, idx) => {
                      const cls = txClass(val.TransactionType);
                      return (
                        <tr key={val.OID ?? idx}>
                          <td>{indexOfFirst + idx + 1}</td>
                          <td>{formatISODate(val.DateDone)}</td>
                          <td>{val.PolicyNo ?? '--'}</td>
                          <td>{val.Narration ?? '--'}</td>
                          <td>{val.Dealer_name ?? '--'}</td>
                          <td>{formatISODate(val.CommencementDate)}</td>
                          <td>{formatISODate(val.ExpiryDate)}</td>
                          <td>
                            {formatKES(val.PaidAmount)}
                          </td>
                        
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                  
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              <div className="wallet-pagination">
                <span className="wallet-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfFirst + ROWS_PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="wallet-pagination-controls">
                  <button
                    className="wallet-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="wallet-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`wallet-page-btn${currentPage === p ? ' active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                  )}
                  <button
                    className="wallet-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Top-Up Modal ── */}
      {modal && (
        <div className="wallet-modal-overlay">
          <div className="wallet-modal">
            <div className="wallet-modal-header">
              <h4><FaPlus style={{ marginRight: 6 }} /> Top Up Account</h4>
              <button className="wallet-modal-close" onClick={() => setModal(false)} aria-label="Close">&times;</button>
            </div>

            {/* Disabled notice */}
            {/* <div className="wallet-disabled-notice">
              <span>⚠</span>
              <p style={{ margin: 0 }}>Online top-up is currently disabled. Please use the M-PESA Paybill option below or contact support.</p>
            </div> */}

            {/* Paybill instructions */}
            <div className="wallet-paybill">
              <p className="wallet-paybill-title">Pay via M-PESA Paybill</p>
              <p>1. Go to M-PESA &rarr; Lipa na M-PESA &rarr; Paybill</p>
              <p>2. Business No: <strong>200300</strong></p>
              <p>3. Account No: <strong>{walletNo || '—'}</strong></p>
              <p>4. Enter amount &amp; M-PESA PIN, then confirm</p>
            </div>

            {/* Form (disabled) */}
          
          </div>
        </div>
      )}
    </div>
  );
}
